import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import { Box, Chip, DialogContent, DialogTitle, IconButton, TableBody, TableRow, styled } from '@mui/material';
import { useOverviewState } from '../useOverviewProvider';
import { DOMAIN_NAME } from 'dashboard/common';
import { DASHBOARD_OVERVIEW_ATTENDANCE, DASHBOARD_OVERVIEW_LEAVE } from 'dashboard/redux.datasets';
import { useSelector } from 'react-redux';
import { datasetLoadingSelector } from '@libs/datasets';
import { ContentCircularLoader } from '@libComponents';
import { theme } from 'app/Theme';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo } from 'react';

const ATTENDANCE_COLUMNS = [
    { label: 'Name', width: '35%' },
    { label: 'Designation', width: '35%' },
    { label: 'Branch', width: '30%' }
];
const LEAVE_COLUMNS = [
    { label: 'Name', width: '30%' },
    { label: 'Leave Type', width: '22%' },
    { label: 'Designation', width: '28%' },
    { label: 'Branch', width: '20%' }
];

const UNCATEGORIZED_LEAVE = 'Other';

const LEAVE_TYPE_COLORS = {
    Casual: { bg: '#FFF3E0', border: '#EF6C00', text: '#BF360C' },
    'Compensatory Leave': { bg: '#F3E5F5', border: '#7B1FA2', text: '#4A148C' },
    Compensatory: { bg: '#F3E5F5', border: '#7B1FA2', text: '#4A148C' },
    Earned: { bg: '#FFFDE7', border: '#F9A825', text: '#F57F17' },
    Maternity: { bg: '#FCE4EC', border: '#C2185B', text: '#880E4F' },
    Paternity: { bg: '#FCE4EC', border: '#C2185B', text: '#880E4F' },
    Sick: { bg: '#FFEBEE', border: '#D32F2F', text: '#B71C1C' },
    Other: { bg: '#ECEFF1', border: '#78909C', text: '#37474F' }
};

const getLeaveStyle = (category) => {
    if (!category) return LEAVE_TYPE_COLORS.Other;
    const trimmed = category.trim();
    if (LEAVE_TYPE_COLORS[trimmed]) return LEAVE_TYPE_COLORS[trimmed];
    const lower = trimmed.toLowerCase();
    if (lower.includes('casual')) return LEAVE_TYPE_COLORS.Casual;
    if (lower.includes('sick')) return LEAVE_TYPE_COLORS.Sick;
    if (lower.includes('earned')) return LEAVE_TYPE_COLORS.Earned;
    if (lower.includes('comp')) return LEAVE_TYPE_COLORS['Compensatory Leave'];
    if (lower.includes('matern')) return LEAVE_TYPE_COLORS.Maternity;
    if (lower.includes('patern')) return LEAVE_TYPE_COLORS.Paternity;
    return LEAVE_TYPE_COLORS.Other;
};

const LeaveChip = ({ category }) => {
    const style = getLeaveStyle(category);
    return (
        <Chip
            label={category || UNCATEGORIZED_LEAVE}
            size="small"
            sx={{
                backgroundColor: style.bg,
                color: style.text,
                borderColor: style.border,
                borderWidth: 1,
                borderStyle: 'solid',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
                borderRadius: '12px'
            }}
        />
    );
};

const StyledBox = styled(Box)(
    ({ theme: { palette, typography, shape } }) => ({
        width: '100%',
        overflow: 'auto',
        minHeight: 300,
        position: "relative",
        maxWidth: "50vw",
        '& table': {
            tableLayout: 'fixed'
        },
        '@media screen and (max-width: 768px)': {
            maxWidth: '100vw'
        }
    })
);

const LEAVE_STATUS = {
    'all': {
        label: 'Total Employee',
        color: 'db_overview_1'
    },
    'in': {
        label: 'Present',
        color: 'db_overview_2'
    },
    'absent': {
        label: 'Absent',
        color: 'db_overview_3'
    },
    'arrivedLate': {
        label: 'Late',
        color: 'db_overview_4'
    },
    'leave': {
        label: 'Leaves',
        color: 'db_overview_5'
    },
    'inOffice': {
        label: 'In Office',
        color: 'db_overview_6'
    },
    "remote": {
        label: 'Remotely Joined',
        color: "db_overview_7"
    }
};

const EmployeeRow = ({ row, showLeaveType }) => (
    <TableRow hover>
        <TableCellContainer sx={{ pl: 2 }}>{row.name || '-'}</TableCellContainer>
        {showLeaveType && (
            <TableCellContainer sx={{ pl: 2 }}>
                <LeaveChip category={row.categoryName} />
            </TableCellContainer>
        )}
        <TableCellContainer sx={{ pl: 2 }}>{row.designation || '-'}</TableCellContainer>
        <TableCellContainer sx={{ pl: 2 }}>{row.branch || '-'}</TableCellContainer>
    </TableRow>
);

const DataTable = (({ onClose, ...props }) => {
    const { attendanceList, leaveList, status } = useOverviewState();

    let isLoading = useSelector((state) =>
        datasetLoadingSelector(state, DOMAIN_NAME, DASHBOARD_OVERVIEW_ATTENDANCE) ||
        datasetLoadingSelector(state, DOMAIN_NAME, DASHBOARD_OVERVIEW_LEAVE)
    );

    const isLeave = status === 'leave';

    // Memoized so the fallback doesn't hand out a fresh [] every render
    const data = useMemo(
        () => (isLeave ? leaveList.data : attendanceList.data) || [],
        [isLeave, leaveList.data, attendanceList.data]
    );

    const columns = isLeave ? LEAVE_COLUMNS : ATTENDANCE_COLUMNS;

    return (
        <StyledBox>
            {isLoading && <ContentCircularLoader />}
            {!isLoading &&
                (<>
                    <DialogTitle sx={{
                        textAlign: "left",
                        backgroundColor: theme.palette[LEAVE_STATUS[status].color].light,
                        color: theme.palette[LEAVE_STATUS[status].color].dark,
                    }} >
                        {LEAVE_STATUS[status].label}
                        {" (" + data.length + ")"}
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent sx={{
                        maxHeight: '70vh',
                        overflow: 'auto'
                    }}>
                        <TableContainer >
                            <TableHeaderContainer sx={{ backgroundColor: theme.palette.background.default }}>
                                {columns.map((col) => (
                                    <TableCellContainer
                                        sx={{ pl: 2, fontWeight: 'bold', width: col.width }}
                                        typography="caption"
                                        key={col.label}
                                    >
                                        {col.label}
                                    </TableCellContainer>
                                ))}
                            </TableHeaderContainer>
                            <TableBody>
                                {data.map((row, i) => (
                                    <EmployeeRow
                                        key={row.id || i}
                                        row={row}
                                        showLeaveType={isLeave}
                                    />
                                ))}
                            </TableBody>
                        </TableContainer>
                    </DialogContent>
                </>)
            }
        </StyledBox>
    );
});

export default DataTable;

